import React, { ComponentType, Dispatch, SetStateAction } from 'react';
import { State } from './State';
import {
  Alert,
  Checkbox,
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';

type Props = {
  formState: State;
  setFormState: Dispatch<SetStateAction<State>>;
};

export const ServiceProviderPicker: ComponentType<Props> = ({
  formState: { serviceProviders },
  setFormState,
}) => {
  const anyServiceProviderSelected =
    serviceProviders &&
    serviceProviders.some((serviceProvider) => serviceProvider.selected);

  const everyServiceProviderSelected =
    anyServiceProviderSelected &&
    serviceProviders.every((serviceProvider) => serviceProvider.selected);

  return serviceProviders ? (
    serviceProviders.length ? (
      <Select
        sx={{ width: '100%' }}
        defaultOpen
        multiple
        value={
          serviceProviders.filter(
            (serviceProvider) => serviceProvider.selected
          ) as Array<
            typeof serviceProviders[0] | null | typeof serviceProviders
          >
        }
        displayEmpty
        renderValue={(selectedServiceProviders) => {
          if (selectedServiceProviders.length === 0) {
            return <em>Please select at least one service provider</em>;
          }

          const displayedProviders = 3;

          return (
            selectedServiceProviders
              .slice(0, displayedProviders)
              .map(({ value: { name } }: any) => name)
              .join(', ') +
            (selectedServiceProviders.length > displayedProviders
              ? `, and ${
                  selectedServiceProviders.length - displayedProviders
                } more...`
              : '')
          );
        }}
        onChange={({ target: { value } }) => {
          if (serviceProviders !== undefined && typeof value !== 'string') {
            const selectedServiceProviders = value.includes(null)
              ? []
              : value.flat();

            setFormState((formState) => ({
              ...formState,
              serviceProviders: serviceProviders.map((serviceProvider) => ({
                ...serviceProvider,
                selected: selectedServiceProviders.includes(serviceProvider),
              })),
            }));
          }
        }}
      >
        <MenuItem
          value={
            everyServiceProviderSelected ? null : (serviceProviders as any)
          }
        >
          <Checkbox
            checked={everyServiceProviderSelected}
            indeterminate={
              anyServiceProviderSelected && !everyServiceProviderSelected
            }
          />
          <ListItemText primary="Select all" />
        </MenuItem>
        {serviceProviders.map((serviceProvider: any) => (
          <MenuItem key={serviceProvider.value.id} value={serviceProvider}>
            <Checkbox checked={serviceProvider.selected} />
            <ListItemText primary={serviceProvider.value.name} />
          </MenuItem>
        ))}
      </Select>
    ) : (
      <Alert severity="error">
        No confirmed service reservations found in the selected date range.
      </Alert>
    )
  ) : (
    <CircularProgress />
  );
};
